import React from 'react';
import PropTypes from 'prop-types';
import { compose, withProps, withState, lifecycle } from 'recompose';
import { groupBy, map, sortBy, pipe, entries } from 'lodash/fp';
import { Portal } from 'react-portal';
import classnames from 'classnames';
import { t } from '../../../modules/infra/i18n';
import ShopNameSwitcherListItem from './ShopNameSwitcherListItem';

const MAX_SHOPS_IN_PLAIN_VIEW = 10;

const renderPlainList = (items, onChange) => pipe(
  sortBy([item => (item.alias || item.name).toUpperCase()]),
  map(item => (
    <ShopNameSwitcherListItem
      key={item.id}
      onClick={() => onChange(item)}
      item={item}
    />
  ))
)(items);

const renderGroupedList = (items, onChange) => pipe(
  groupBy(item => (item.alias || item.name || ' ').toUpperCase()[0]),
  entries,
  sortBy([([key]) => key.toUpperCase()]),
  map(([firstLetter, groupedItems]) => (
    <section key={firstLetter}>
      <header className="c-shops-navigation__shop-first-letter">
        {firstLetter}
      </header>
      {renderPlainList(groupedItems, onChange)}
    </section>
  ))
)(items);

const ShopNameSwitcher = (props) => {
  const { activeItem, inactiveItems, isGroupedView, setSearchString, brandName, brandItem } = props;

  const renderFunction = isGroupedView ? renderGroupedList : renderPlainList;

  const wrapperClassNames = classnames(props.className, 'ignore-react-onclickoutside');

  return (
    <Portal>
      <section className={wrapperClassNames}>
        <h2 className="c-shops-navigation__header">{brandName}</h2>
        {brandItem && (
          <ul className="c-shops-navigation__list-brand">
            <ShopNameSwitcherListItem
              onClick={() => props.onChange(brandItem)}
              item={brandItem}
              active={!activeItem}
            />
          </ul>
        )}
        {!!(brandItem && !isGroupedView) && (
          <h3 className="c-shops-navigation__location-header">{t('shops:shop_switcher.locations_header')}</h3>
        )}
        {isGroupedView && (
          <input
            type="text"
            placeholder={t('shops:shop_switcher.search_placeholder')}
            className="c-shops-navigation__search-input"
            onChange={e => setSearchString(e.target.value)}
          />
        )}
        <ul className="c-shops-navigation__list">
          {activeItem && (
            <ShopNameSwitcherListItem
              onClick={() => props.onChange(activeItem)}
              item={activeItem}
              active
            />
          )}
          {renderFunction(inactiveItems, props.onChange)}
        </ul>
      </section>
    </Portal>
  );
};

ShopNameSwitcher.propTypes = {
  className: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isGroupedView: PropTypes.bool.isRequired,
  activeItem: PropTypes.object.isRequired,
  inactiveItems: PropTypes.array.isRequired,
  brandItem: PropTypes.object.isRequired,
  setSearchString: PropTypes.func.isRequired,
  brandName: PropTypes.string.isRequired,
};

export default compose(
  withState('searchString', 'setSearchString', ''),
  withProps((props) => {
    const { items, activeId, searchString, brandItem } = props;
    const activeItem = items.find(item => activeId === item.id);
    const inactiveItems = searchString
      ? items.filter(
        item => item !== activeItem && (item.alias || item.name).toUpperCase().includes(searchString.toUpperCase())
      )
      : items.filter(item => item !== activeItem);
    const isGroupedView = items.length >= MAX_SHOPS_IN_PLAIN_VIEW;
    const isBrand = !!brandItem;

    return ({
      activeItem,
      inactiveItems,
      isGroupedView,
      brandName: isBrand ? brandItem.brandName : (activeItem && activeItem.brandName),
    });
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      if (prevProps.activeId !== this.props.activeId) {
        this.props.setSearchString('');
      }
    },
  })
)(ShopNameSwitcher);
